import { createSlice } from '@reduxjs/toolkit';

/**
 * Blue Cart slice — RFQ (Request for Quotation) workflow.
 * v2 — supports multiple configuration instances per product (different S#, thickness, etc.)
 * Each cart item has a unique `cartItemId` (uuid-lite) so one product can have N entries.
 */

function makeId() {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

const initialState = {
  items: [],
  // Each item: { cartItemId, product, quantity, configuration, notes }
  status: 'draft',
  lastSubmittedOrderId: null,
};

const blueCartSlice = createSlice({
  name: 'blueCart',
  initialState,
  reducers: {
    addItem: {
      reducer(state, action) {
        const { product, quantity, configuration, notes } = action.payload;
        state.items.push({
          cartItemId: makeId(),
          product,
          quantity: quantity || 1,
          configuration: configuration || {},
          notes: notes || '',
        });
      },
      prepare(product, quantity = 1, configuration = {}, notes = '') {
        return { payload: { product, quantity, configuration, notes } };
      },
    },
    removeItem(state, action) {
      // action.payload = cartItemId
      state.items = state.items.filter((i) => i.cartItemId !== action.payload);
    },
    updateQuantity(state, action) {
      const { cartItemId, quantity } = action.payload;
      const item = state.items.find((i) => i.cartItemId === cartItemId);
      if (item) item.quantity = Math.max(1, quantity);
    },
    updateNotes(state, action) {
      const { cartItemId, notes } = action.payload;
      const item = state.items.find((i) => i.cartItemId === cartItemId);
      if (item) item.notes = notes;
    },
    updateConfiguration(state, action) {
      const { cartItemId, configuration } = action.payload;
      const item = state.items.find((i) => i.cartItemId === cartItemId);
      if (item) item.configuration = { ...item.configuration, ...configuration };
    },
    setStatus(state, action) {
      state.status = action.payload;
    },
    submitSucceeded(state, action) {
      state.status = 'submitted';
      state.lastSubmittedOrderId = action.payload?.orderId || null;
    },
    clearCart(state) {
      state.items = [];
      state.status = 'draft';
      state.lastSubmittedOrderId = null;
    },
  },
});

export const {
  addItem: addBlueItem,
  removeItem: removeBlueItem,
  updateQuantity: updateBlueQuantity,
  updateNotes: updateBlueNotes,
  updateConfiguration: updateBlueConfiguration,
  setStatus: setBlueStatus,
  submitSucceeded: blueSubmitSucceeded,
  clearCart: clearBlueCart,
} = blueCartSlice.actions;

export const selectBlueCart      = (s) => s.blueCart;
export const selectBlueItems     = (s) => s.blueCart.items;
export const selectBlueItemCount = (s) =>
  s.blueCart.items.reduce((sum, i) => sum + i.quantity, 0);
export const selectBlueStatus    = (s) => s.blueCart.status;

export default blueCartSlice.reducer;
